import React, { useMemo } from 'react';
import { TreeSelect } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';

/** ===== Types ===== */
export interface OptionSelect_V2 {
    key: number;
    value: number;
    label: string;                 // không dùng bởi TreeSelect
    title: React.ReactNode;
    children?: OptionSelect_V2[];
    level: number;
    parentId?: number;
    disabled?: boolean;
    disableCheckbox?: boolean;
    selectable?: boolean;          // hỗ trợ leaf-only
}

type DisableMode = 'disabled' | 'disableCheckbox';

interface TreeSelectCustomProps {
    data: OptionSelect_V2[];
    value: number[];                           // mảng id
    onChange: (vals: number[]) => void;        // mảng id gọn
    placeholder?: string;
    disabledIds?: number[];
    cascadeDisable?: boolean;
    disableMode?: DisableMode;
    allowClear?: boolean;
    className?: string;
    onlyLeafSelectable?: boolean;              // chỉ cho chọn lá?
}

type LabeledValue = { value: number | string; label?: React.ReactNode; halfChecked?: boolean };

const markParentsWithSelectedChildren = (
    tree: OptionSelect_V2[],
    selectedIds: number[],
    index: Indexes
): OptionSelect_V2[] => {
    const selected = new Set(selectedIds);

    return tree.map(n => {
        const hasSelectedChild = (index.descendants.get(n.value) ?? []).some(id => selected.has(id));
        const isSelfSelected = selected.has(n.value);

        const extraClass =
            hasSelectedChild && !isSelfSelected
                ? 'tree-select-custom__has-child-selected'
                : undefined;

        return {
            ...n,
            className: extraClass,
            children: n.children
                ? markParentsWithSelectedChildren(n.children, selectedIds, index)
                : undefined,
        };
    });
};


/** ===== Disable + leaf-only patch ===== */
// const applyDisable = (
//     nodes: OptionSelect_V2[],
//     disabledIds: number[] = [],
//     cascade = true,
//     mode: DisableMode = 'disabled',
//     ancestorDisabled = false,
//     onlyLeafSelectable = false
// ): OptionSelect_V2[] => {
//     return nodes.map(n => {
//         const selfDisabled = disabledIds.includes(n.value);
//         const effectiveDisabled = cascade ? (ancestorDisabled || selfDisabled) : selfDisabled;

//         const isLeaf = !n.children || n.children.length === 0;

//         const patch: Partial<OptionSelect_V2> =
//             mode === 'disabled'
//                 ? { disabled: effectiveDisabled, disableCheckbox: undefined }
//                 : { disableCheckbox: effectiveDisabled, disabled: undefined };

//         if (onlyLeafSelectable && !isLeaf) {
//             patch.selectable = false; // không cho chọn node không phải lá
//             if (mode === 'disableCheckbox') patch.disableCheckbox = true;
//         }

//         const children = n.children?.length
//             ? applyDisable(n.children, disabledIds, cascade, mode, effectiveDisabled, onlyLeafSelectable)
//             : undefined;

//         return { ...n, ...patch, children };
//     });
// };

const applyDisable = (
    nodes: OptionSelect_V2[],
    disabledIds: number[] = [],
    cascade = true,
    mode: DisableMode = 'disabled',
    ancestorDisabled = false,
    onlyLeafSelectable = false
): OptionSelect_V2[] => {
    return nodes.map(n => {
        const selfDisabled = disabledIds.includes(n.value);

        // Node hiện tại bị khóa nếu:
        // - Tổ tiên đã bị khóa (ancestorDisabled), hoặc
        // - Chính node này nằm trong disabledIds
        const effectiveDisabled = cascade ? (ancestorDisabled || selfDisabled) : selfDisabled;

        const isLeaf = !n.children || n.children.length === 0;

        const patch: Partial<OptionSelect_V2> =
            mode === 'disabled'
                ? { disabled: effectiveDisabled, disableCheckbox: undefined }
                : { disableCheckbox: effectiveDisabled, disabled: undefined };

        if (onlyLeafSelectable && !isLeaf) {
            patch.selectable = false;
            if (mode === 'disableCheckbox') patch.disableCheckbox = true;
        }

        // 🔑 Điểm khác biệt: KHÔNG truyền selfDisabled xuống con.
        // Chỉ truyền “ancestorDisabled” (khóa do tổ tiên xa hơn) cho đời sau.
        const nextAncestorDisabled = cascade ? ancestorDisabled : false;

        const children = n.children?.length
            ? applyDisable(n.children, disabledIds, cascade, mode, nextAncestorDisabled, onlyLeafSelectable)
            : undefined;

        return { ...n, ...patch, children };
    });
};


/** ===== Flatten để lọc orphan ===== */
const flattenValues = (nodes: OptionSelect_V2[], bag: Set<number> = new Set()): Set<number> => {
    for (const n of nodes) {
        bag.add(n.value);
        if (n.children?.length) flattenValues(n.children, bag);
    }
    return bag;
};

/** ===== Chỉ mục cây để cascade ===== */
type Indexes = {
    byId: Map<number, OptionSelect_V2>;
    parent: Map<number, number | null>;
    children: Map<number, number[]>;
    descendants: Map<number, number[]>;
    canSelect: (id: number) => boolean;
};

const buildIndexes = (nodes: OptionSelect_V2[]): Indexes => {
    const byId = new Map<number, OptionSelect_V2>();
    const parent = new Map<number, number | null>();
    const children = new Map<number, number[]>();

    const dfs = (arr: OptionSelect_V2[], p: number | null) => {
        for (const n of arr) {
            byId.set(n.value, n);
            parent.set(n.value, p);
            if (n.children?.length) {
                const ids = n.children.map(c => c.value);
                children.set(n.value, ids);
                dfs(n.children, n.value);
            } else {
                children.set(n.value, []);
            }
        }
    };
    dfs(nodes, null);

    const descendants = new Map<number, number[]>();
    for (const id of byId.keys()) {
        const d: number[] = [];
        const stack = [...(children.get(id) ?? [])];
        while (stack.length) {
            const x = stack.pop()!;
            d.push(x);
            stack.push(...(children.get(x) ?? []));
        }
        descendants.set(id, d);
    }

    const canSelect = (id: number) => {
        const n = byId.get(id);
        if (!n) return false;
        if (n.disabled) return false;
        if (n.disableCheckbox) return false;
        if (n.selectable === false) return false;
        return true;
    };

    return { byId, parent, children, descendants, canSelect };
};

// helper: có con trực tiếp bị khóa không?
const hasImmediateBlockedChild = (id: number, index: Indexes) => {
    const kids = index.children.get(id) ?? [];
    for (const k of kids) {
        if (!index.canSelect(k)) return true; // disabled/disableCheckbox/selectable=false
    }
    return false;
};

//// Thay thế isFullySelected bằng:
// const isFullySelected = (id: number, selected: Set<number>, index: Indexes) => {
//     const ds = index.descendants.get(id) ?? [];
//     let hasSelectableDesc = false;

//     for (const x of ds) {
//         if (!index.canSelect(x)) continue;
//         hasSelectableDesc = true;
//         if (!selected.has(x)) return false;
//     }

//     // Nếu không có descendant có thể chọn (tức lá), "full" = chính node đang được chọn
//     if (!hasSelectableDesc) {
//         return selected.has(id);
//     }
//     return true;
// };

// NEW isFullySelected: block nếu có con trực tiếp bị khóa
const isFullySelected = (id: number, selected: Set<number>, index: Indexes) => {
    // 1) Nếu có ít nhất một con trực tiếp bị khóa → KHÔNG full
    if (hasImmediateBlockedChild(id, index)) return false;

    // 2) Kiểm tra descendants có thể chọn: tất cả đều phải đang được chọn
    const ds = index.descendants.get(id) ?? [];
    let hasSelectableDesc = false;

    for (const x of ds) {
        if (!index.canSelect(x)) continue;
        hasSelectableDesc = true;
        if (!selected.has(x)) return false;
    }

    // 3) Nếu không có descendant có thể chọn (tức lá), "full" = chính node đang được chọn
    if (!hasSelectableDesc) {
        return selected.has(id);
    }
    return true;
};

const isLeafByIndex = (id: number, index: Indexes) => (index.children.get(id) ?? []).length === 0;

/** ===== Handler onChange tách riêng cho strict mode ===== */
export function handleStrictCascadeChange(params: {
    prevSelected: number[];         // state trước đó
    strictVals: LabeledValue[];     // vals từ TreeSelect (strict)
    extra: any;                     // extra từ TreeSelect
    index: Indexes;                 // chỉ mục cây
    onlyLeafSelectable?: boolean;   // (không dùng để ảnh hưởng cha)
    outputMode?: 'raw' | 'leaf' | 'compress'; // tuỳ chọn xuất (mặc định raw)
}): number[] {
    const { prevSelected, strictVals, extra, index, onlyLeafSelectable, outputMode = 'raw' } = params;

    // Giữ lại các id còn tồn tại trong cây
    const next = new Set<number>(prevSelected.filter(id => index.byId.has(id)));

    const triggerId = Number(extra?.triggerValue);
    const checked: boolean | undefined = extra?.checked;

    // fallback: nếu không xác định được trigger, đồng bộ “thẳng” theo strictVals
    if (!Number.isFinite(triggerId) || typeof checked !== 'boolean') {
        const direct = (Array.isArray(strictVals) ? strictVals : []).map(v => Number(v.value));
        return postProcessOutput(direct, index, outputMode);
    }

    const addIfAllowed = (id: number) => { if (index.canSelect(id)) next.add(id); };
    const delIfAllowed = (id: number) => {
        const n = index.byId.get(id);
        if (n && !n.disableCheckbox && !n.disabled) next.delete(id);
    };

    if (checked) {
        // Tick node + toàn bộ descendants
        addIfAllowed(triggerId);
        for (const d of index.descendants.get(triggerId) ?? []) addIfAllowed(d);

        // ❌ KHÔNG cập nhật ancestor nữa
        // if (!onlyLeafSelectable) { ... }  // đã bỏ
    } else {
        // Un-tick node + toàn bộ descendants
        delIfAllowed(triggerId);
        for (const d of index.descendants.get(triggerId) ?? []) delIfAllowed(d);

        // ❌ KHÔNG cập nhật ancestor nữa
        // if (!onlyLeafSelectable) { ... }  // đã bỏ
    }

    const result = [...next];
    return postProcessOutput(result, index, outputMode);
}


/** ===== Hậu xử lý giá trị trả ra (tuỳ chọn) ===== */
function postProcessOutput(vals: number[], index: Indexes, mode: 'raw' | 'leaf' | 'compress'): number[] {
    if (mode === 'leaf') {
        return vals.filter(id => isLeafByIndex(id, index));
    }
    if (mode === 'compress') {
        // nén: nếu một node FULL thì giữ node đó và bỏ toàn bộ descendants của nó
        const set = new Set(vals);
        // sắp xếp theo độ sâu tăng dần (cha xét sau cùng để có đủ trạng thái con)
        const depth = (id: number) => {
            let d = 0, p = index.parent.get(id) ?? null;
            while (p !== null) { d++; p = index.parent.get(p) ?? null; }
            return d;
        };
        const sorted = [...set].sort((a, b) => depth(a) - depth(b));
        for (const id of sorted) {
            if (isFullySelected(id, set, index)) {
                for (const d of index.descendants.get(id) ?? []) set.delete(d);
            }
        }
        return [...set];
    }
    return vals; // raw
}

/** ===== Component ===== */
export const TreeSelectCustom_V2: React.FC<TreeSelectCustomProps> = ({
    data,
    value,
    onChange,
    placeholder = 'Chọn phạm vi hoạt động',
    disabledIds = [],
    cascadeDisable = true,
    disableMode = 'disabled',
    allowClear = true,
    className,
    onlyLeafSelectable = false,
}) => {
    // 1) build treeData (disable + leaf-only)
    const treeData = useMemo(
        () => applyDisable(data, disabledIds, cascadeDisable, disableMode, false, onlyLeafSelectable),
        [data, disabledIds, cascadeDisable, disableMode, onlyLeafSelectable]
    );

    // 2) lọc orphan ids
    const validIdSet = useMemo(() => flattenValues(treeData), [treeData]);
    const filteredValue = useMemo(() => value?.filter(v => validIdSet.has(v)) ?? [], [value, validIdSet]);

    // 3) index để cascade
    const index = useMemo(() => buildIndexes(treeData), [treeData]);

    // 4) strict value cho TreeSelect
    const strictValue: LabeledValue[] = useMemo(
        () => filteredValue.map(v => ({ value: v })),
        [filteredValue]
    );

    // const displayTreeData = useMemo(() => markSelectedParent(treeData, filteredValue, index), [treeData, filteredValue, index]);
    const displayTreeData = useMemo(
        () => markParentsWithSelectedChildren(treeData, filteredValue, index),
        [treeData, filteredValue, index]
    );

    return (
        <TreeSelect<LabeledValue[]>
            className={className}
            treeData={displayTreeData}
            treeCheckable
            treeCheckStrictly
            value={strictValue}
            onChange={(vals, _labels, extra) => {
                const newIds = handleStrictCascadeChange({
                    prevSelected: filteredValue,
                    strictVals: (Array.isArray(vals) ? vals : []) as LabeledValue[],
                    extra,
                    index,
                    onlyLeafSelectable,
                    // chọn 1 trong 3: 'raw' | 'leaf' | 'compress'
                    // raw: giữ nguyên kết quả tick
                    // leaf: chỉ trả lá
                    // compress: nén full-branch về id cha
                    outputMode: 'raw',
                });
                onChange(newIds);
            }}
            placeholder={placeholder}
            allowClear={allowClear}
            // Ẩn toàn bộ tag
            maxTagCount={0}
            maxTagPlaceholder={() => null}
            // Bật tìm kiếm
            showSearch
            filterTreeNode={(input, treeNode) =>
                String(treeNode.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
            }
        // Tối ưu dropdown (tuỳ chọn)
        // dropdownMatchSelectWidth={false}
        // listHeight={320}
        // treeDefaultExpandAll
        />
    );
};
